'use client';

import { useState, useRef } from 'react';
import { styled } from '@linaria/react';
import { useMyTrackDate } from '@fe-news/context/my-track-date-provider';
import Image from 'next/image';
import { MyTrackSelectDate } from '@fe-news/constants/my-track/type';
import useClickOutside from '@fe-common-utils/libs/hooks/use-click-outside';
import cx from 'classnames';

const DateContainer = styled.div`
  position: relative;
`;

const Container = styled.button<{ isOpenMore: boolean }>`
  display: flex;
  align-items: center;
  column-gap: 8px;
  color: ${({ isOpenMore }) => (isOpenMore ? '#515c69' : '#848d97')};
  font-size: 13px;
  line-height: 1.54;
  letter-spacing: 0.5px;
  cursor: pointer;

  & > span {
    margin-right: 2px;
  }
`;

const MoreDropDown = styled.ul`
  z-index: 3;
  box-sizing: border-box;
  overflow: hidden;
  position: absolute;
  top: 30px;
  left: -10px;
  border-radius: 6px;
  background-color: #fff;
  transition: all 0.3s ease 0s;
  box-shadow: 0 0 6px 0 #00418f19;
  border: solid 1px #e2e8f1;
  padding: 4px 6px;
`;

const MoreDropDownItem = styled.li`
  padding: 10px 20px;
  cursor: pointer;
  display: block;
  font-size: 16px;
  line-height: 1;
  text-align: center;
  color: #383838;
  border-bottom: solid 1px #e2e8f1;
  min-width: 90px;
  font-weight: 400;

  &:last-child {
    border-bottom: none;
  }

  &.active {
    color: #e03f19;
  }
`;

const MyTrackDatePicker = () => {
  const { selectDate, setSelectDate } = useMyTrackDate();
  const [isOpenMore, setIsOpenMore] = useState<boolean>(false);
  const dropdownReference = useRef(null);

  const onClickMore = () => {
    setIsOpenMore(previous => !previous);
  };

  const onClickOutside = () => {
    setIsOpenMore(false);
  };

  useClickOutside(dropdownReference, onClickOutside, true);

  const onClickDate = (date: MyTrackSelectDate) => {
    setSelectDate(date);
    setIsOpenMore(false);
  };

  return (
    <DateContainer ref={dropdownReference}>
      <Container onClick={onClickMore} isOpenMore={isOpenMore} aria-label='search date'>
        <Image
          src={isOpenMore ? '/assets/icons/date/calendar-active.svg' : '/assets/icons/date/calendar.svg'}
          width={16}
          height={16}
          alt='calendar-icon'
        />
        <span>{selectDate}</span>
        <Image
          src={isOpenMore ? '/assets/icons/triangle-active.svg' : '/assets/icons/triangle-default.svg'}
          width={10}
          height={6}
          alt='arrow'
        />
      </Container>
      {isOpenMore && (
        <MoreDropDown>
          {(Object.keys(MyTrackSelectDate) as Array<keyof typeof MyTrackSelectDate>).map(date => (
            <MoreDropDownItem
              key={date}
              className={cx({ active: MyTrackSelectDate[date] === selectDate })}
              onClick={() => onClickDate(MyTrackSelectDate[date])}
              aria-label={MyTrackSelectDate[date]}
            >
              {MyTrackSelectDate[date]}
            </MoreDropDownItem>
          ))}
        </MoreDropDown>
      )}
    </DateContainer>
  );
};

export default MyTrackDatePicker;
